import { ReparoApi } from './index';

export type MaterialType = 'RM' | 'PM';

export type BomRow = {
    productId: string;
    productSku: string | null;
    productName: string;
    materialId: string;
    materialSku: string | null;
    materialName: string;
    materialType: MaterialType;
    unit: string;
    quantity: number;
    /**
     * The vendor this line belongs to. NULL means it applies to ALL VENDORS — including
     * in-house production, which has no vendor at all — and that is what nearly every line
     * here is.
     *
     * A product two vendors make differently therefore returns the same material more than
     * once, one line per vendor. Group by this before summing.
     */
    vendorId: string | null;
    vendorName: string | null;
};

/** Null is "applies to all vendors", and a picker option needs a value it can hold. */
export const ALL_VENDORS = '__all_vendors__';

export type BomUploadRowError = {
    row: number;
    productId?: string;
    productSku?: string;
    materialSku?: string;
    materialName?: string;
    reason: string;
};

export type BomUploadResult = {
    processed: number;
    errors: BomUploadRowError[];
};

export type BomProductPick = {
    productId: string;
    sku: string | null;
    name: string;
    /** Catalog GST slab / HSN, for seeding order lines. Null = nobody stated one. */
    gstRate?: number | null;
    hsnCode?: string | null;
    /** Per-unit volume in CBM, so an order line can total its own. Null = not on file. */
    volumeCbm?: number | null;
    /** Units in one case, so a line can be typed in cases. Null = no case pack on file. */
    unitsPerCase?: number | null;
};

export type BomMaterialPick = {
    materialId: string;
    gstRate?: number | null;
    hsnCode?: string | null;
    sku: string | null;
    name: string;
    materialType: MaterialType;
    unit: string;
};

export const getBom = async (): Promise<BomRow[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/product-material-mappings/bom',
    });
};

/**
 * Products that have at least one BOM line. Pickers that must restrict to things the brand
 * actually assembles (Work Orders) use it.
 *
 * Derived from getBom() so it shares the BOM screen's cache — which also means it is NOT
 * filtered to active products the way getBomProductPicks is, because BOM rows carry no
 * activity flag. A retired SKU that still has a BOM appears here (4 do at Troovy).
 */
export const getProductsWithBom = async (): Promise<BomProductPick[]> => {
    const bom = await getBom();
    const seen = new Set<string>();
    const products: BomProductPick[] = [];
    for (const row of bom) {
        if (!row.productId || seen.has(row.productId)) continue;
        seen.add(row.productId);
        products.push({
            productId: row.productId,
            sku: row.productSku,
            name: row.productName,
        });
    }
    return products;
};

export const uploadBomCsv = async (file: File): Promise<BomUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: '/product-material-mappings/upload-csv',
        body: formData,
    });
};

/**
 * Writes one line. The server upserts on the line's address, so the result says whether the
 * address was empty ('created') or already held a line whose quantity was replaced
 * ('updated'); a screen shows the difference, a file import does not care.
 */
export const createBomMapping = async (payload: {
    productId: string;
    materialId: string;
    quantity: number;
    /** Omit or send null for a line that applies to all vendors. */
    vendorId?: string | null;
}): Promise<{ outcome?: 'created' | 'updated' }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/product-material-mappings',
        body: JSON.stringify(payload),
    });
};

/**
 * The products a picker may offer: ACTIVE ONLY, filtered on the server.
 *
 * A brand retires a SKU by deactivating it, and a retired SKU is not something anyone should
 * be able to write a new BOM line, purchase-order line or transfer against. Troovy lists 607
 * products of which 52 are live — unfiltered, the twelve anybody wanted sat under 555 they
 * did not.
 *
 * Two screens read this as an id-to-name lookup for orders already placed rather than as a
 * picker. Both fall back to the product id, so an order raised before its SKU was retired
 * reads as the code instead of the name.
 */
export const getBomProductPicks = async (): Promise<BomProductPick[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/product-material-mappings/pickers/products',
    });
};

export const getBomMaterialPicks = async (): Promise<BomMaterialPick[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/product-material-mappings/pickers/materials',
    });
};

/*
 * The vendor rides as a query parameter rather than a third path segment: an all-vendors line
 * has no vendor at all, and there is no path segment that means "none" without inventing a
 * sentinel. Absent = the all-vendors line, which is what the route meant before this existed.
 */
const vendorQuery = (vendorId?: string | null) =>
    vendorId ? `?vendorId=${encodeURIComponent(vendorId)}` : '';

export const updateBomQuantity = async (
    productId: string,
    materialId: string,
    quantity: number,
    vendorId?: string | null,
): Promise<void> => {
    return ReparoApi<any>({
        method: 'PATCH',
        url: `/product-material-mappings/${encodeURIComponent(productId)}/${encodeURIComponent(materialId)}${vendorQuery(vendorId)}`,
        body: JSON.stringify({ quantity }),
    });
};

export const deleteBomMapping = async (
    productId: string,
    materialId: string,
    vendorId?: string | null,
): Promise<void> => {
    return ReparoApi<any>({
        method: 'DELETE',
        url: `/product-material-mappings/${encodeURIComponent(productId)}/${encodeURIComponent(materialId)}${vendorQuery(vendorId)}`,
    });
};
